// 2030 Electricity Bill Forecast Data
// All values in pounds (£), real terms (2025)

export const categoryColors = {
  wholesale: '#f07856',      // Coral/orange
  network: '#faaaaf',         // Light pink
  renewables: '#B4F3F3',      // Cyan/turquoise (renamed from Generation Subsidies)
  misc: '#E5CBFF',            // Light purple
  newLevies: '#FFD88D'        // Warm yellow/gold
};

// Bill components structure
export const billComponents = {
  // Wholesale family
  wholesale: {
    categoryId: 'wholesale',
    name: 'Wholesale',
    value2025: 324,
    value2030: 269,
    description: `
    <p> Wholesale prices are influenced by international gas prices and the availability of renewables.</p>
    <p>I spoke to range of GB power market modellers (Modo Energy, Aurora Energy, LCP Delta, and commercial power trading desks). Their central case for the average wholesale price in 2030 was between £60 to £80/MWh.</p>
    <p> This is a little more optimistic than OFGEM's central case of a £20 reduction in combineded wholesale + CfD costs by 2030.</p>
      `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=1866372570#gid=1866372570'
  },

  // Network family
  tnuos: {
    categoryId: 'network',
    name: 'Transmission',
    value2025: 52,
    value2030: 105,
    description: `
    <p>The transmission network sends electricity over long distances. We are about to build a lot more transmission, mostly to connect more offshore wind.</p>
    <p>OFGEM has recently <a href="https://www.ofgem.gov.uk/decision/riio-3-final-determinations-electricity-transmission-gas-distribution-and-gas-transmission-sectors">published a new five-year budget</a> for transmission charges, which shows a big increase to fund grid upgrades. The typical household transmission charge will increase to £105 by 2030.</p>
    <p style="font-style: italic; margin-top: 16px; padding-left: 16px; padding-right: 4px; border-left: 3px solid #ddd;">"RIIO-ET3 will deliver the largest increase in capital spending in the history of the privatised Electricity Transmision sector, potentially reaching [£70bn] over the period." - OFGEM</p>
    <p>Electricity transmission assets get depreciated over 45 years, which means that transmission operators will be able to charge a fixed return on this capital spending for several decades.</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=991360288#gid=991360288'
  },
  duos: {
    categoryId: 'network',
    name: 'Distribution',
    value2025: 114,
    value2030: 146,
    description: `
    <p>The Distribution network delivers electricity locally. It will undergo upgrades for (1) electrified demand, and (2) distribution-connected generation.</p> 
    <p>The next budget for Distribution is not yet published. This estimate is based on a simple first-principles model of estimated capital spending and RAV accumulation. You can examine the assumptions below.</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=1836934830#gid=1836934830',
  },
  bsuos: {
    categoryId: 'network',
    name: 'Balancing',
    value2025: 41,
    value2030: 91,
    description: `
      <p>The grid operator has to match supply and demand across time and space. In real time, it adjusts the outputs of some power plants upwards, and some downwards. </p>
      <p>Balancing has become more expensive as network contraints and wind curtailment have increased.</p>
      <p><strong>Note: balancing costs are expected to peak around 2030, as new transmission arrives afterwards to alleviate constraints.</strong></p>
      <p>Here is NESO's forecast of balancing costs:</p>
      <img src="/neso_balancing.png" alt="NESO Balancing Costs" style="width: 100%; max-width: 500px; margin: 16px 0; border-radius: 4px;" />
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=991360288#gid=991360288'
  },

  // Generation subsidies / Renewables family
  renewablesObligation: {
    categoryId: 'renewables',
    name: 'Renewables Obligation',
    value2025: 102,
    value2030: 17,
    description: `
      <p>The Renewables Obligation was the UK's first renewables subsidy (from 2002 to 2017). It supported many early renewable projects, and most onshore windfarms.</p>
      <p>In 2027, the Renewables Obligation starts winding down, as large projects come off their 20-year contracts. The cost will then decrease every year onwards.</p>
      <p>As of April 2026, some of the Renewables Obligation will be recovered by taxation rather than bills. 75% of domestic RO payments will be covered by the Treasury, with the remaining 25% staying on electricity bills.</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=478398824#gid=478398824&range=B6'
  },
  feedInTariffs: {
    categoryId: 'renewables',
    name: 'Feed in Tariffs',
    value2025: 23,
    value2030: 26,
    description: `
      <p>The Feed-in Tariff gives households with solar panels a payment per kWh of energy generated. This incentivised a lot of people to buy solar panels between 2010-2019.</p>
      <p>Costs will start falling dramatically in 2031 when the first contracts start expiring.</p>
      <p>The Feed-in Tariff is curently inflation-indexed to RPI, not CPI. A policy change is currently being considered here</p>
    `,
  },
  contractsForDifference: {
    categoryId: 'renewables',
    name: 'Contracts for Difference',
    value2025: 33,
    value2030: 70,
    description: `
      <p>Contracts for Difference guarantee generators a fixed price for their electricity.</p>
      <p>The cost of CfDs is dependent on the wholesale price of electricty. If the wholesale price falls steeply, CfDs will be more expensive. If there is another gas crisis, CfDs will pay back to consumers.</p>
      <p>By 2030, ~137 TWh of electricity generation will fall under a CfD (roughly half of total domestic supply).</p>
      <p>Hinkley Point C will start generating ~26 TWh per year under CfD in the 2030s</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=478398824#gid=478398824&range=B37'
  },
  capacityMarket: {
    categoryId: 'renewables',
    name: 'Capacity Market',
    value2025: 28,
    value2030: 45,
    description: `
      <p>The Capacity Market pays dispatchable generation and demand response for standing by during times of peak demand.</p>
      <p>The supply of dispatchable generation has decreased over the past decade, so the cost of the Capacity Market is increasing substantially.</p>
      <p>The cost of the CM will nearly double from next year, and is expected to rise further to 2030. DESNZ plans to introduce a second pricing tier for new build gas generation, because future CM auctions are expected to clear instantly at the maximum price.</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=367315133#gid=367315133'
  },

  // Misc family
  eco: {
    categoryId: 'misc',
    name: 'ECO',
    value2025: 27,
    value2030: 0,
    description: `
      <p>The Energy Company Obligation obliges energy suppliers to pay for insulation and heating upgrades in poorer households.</p>
      <p>Suppliers have to find eligible homes and arrange for improvements like loft insulation, cavity wall filling, or new boilers. The costs are distributed amongst everyone's bills.</p>
      <p>From April 2026, the ECO scheme will be terminated.</p>
    `
  },
  warmHomeDiscount: {
    categoryId: 'misc',
    name: 'Warm Home Discount',
    value2025: 15,
    value2030: 20,
    description: `
      <p>The Warm Home Discount provides a £150 reduction on winter electricity bills to poorer households and pensioners.</p>
      <p>The Warm Homes Discount has been significantly expanded in 25/26, from a budget of ~£600m to £1b.</p>
      <p>It is split between gas and electricity bills, so electricity bills will only see half the cost impact</p>
    `
  },
  smartMeterNetCostCharge: {
    categoryId: 'misc',
    name: 'Smart Meter Net Cost Charge',
    value2025: 20,
    value2030: 20,
    description: `
      <p>Energy suppliers are obgligated to roll out smart meters. They have to buy the meters, pay people to install them, and finance the data systems to collect readings.</p>
      <p>There is no upfront charge to domestic consumers for having a smart meter installed. Therefore, energy suppliers recover the cost from their entire customer base.</p>
    `
  },
  aahedc: {
    categoryId: 'misc',
    name: 'AAHEDC',
    value2025: 1,
    value2030: 1,
    description: `
      <p>This scheme subsidises power lines in remote areas of North Scotland. With few customers and harsh weather, it's expensive to maintain the power grid in remote Highland areas.</p>
      <p>Everyone else pays a small amount on their bill to support this.</p>
    `
  },
  supplierCostsAndMargin: {
    categoryId: 'misc',
    name: 'Supplier costs',
    value2025: 133,
    value2030: 147,
    description: `
      <p>Energy suppliers incur substantial operating costs for customer service, bad debt, staffing, payment processing and more.</p>
      <p>Supplier costs have tended to increase in real terms. In particular, bad debt is currently at historic highs and rising considerably.</p>
      <p>Here we conservatively assume a future real increase of 2%, roughly half that of recent years.</p>
    `
  },

  // New levies family (2030 only)
  sizewellC: {
    categoryId: 'newLevies',
    name: 'Sizewell C',
    value2025: 4,
    value2030: 19,
    description: `
      <p>Sizewell C is the UK's first power plant to be funded under a Regulated Asset Base model.</p>
      <p>The RAB model allows investors to recover their costs from consumer bills and make a fixed return on their spending, before plant operation begins.</p>
      <p>The RAB model was introduced was after the UK struggled to attract private investors to new nuclear projects. It allows Sizewell to start earning revenue during construction. Notably, UK consumers & taxpayers have greater exposure to both returns and overruns.</p>
      `,
      link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=434753547#gid=434753547&range=A1'
  },
  carbonCaptureAllocated: {
    categoryId: 'newLevies',
    name: 'Carbon Capture (allocated)',
    value2025: 0,
    value2030: 4,
    description: `
      <p>The goverment has signed a Dispatchable Power Agreement for a CCGT with CCS in Teeside. It is the only CCS project signed so far.</p>
      <p>It is projected to come online in 2028. It receives Variable Payments and Availability payments</p>
      <p>There is no public information on the expected cost of the project (!), so I have made a first principles estimate. You can exmaine the assumptions below.</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=1007044354#gid=1007044354'
  },
  carbonCaptureRemaining: {
    categoryId: 'newLevies',
    name: 'Carbon Capture (remaining)',
    value2025: 0,
    value2030: 3,
    description: `<p>Around 2/3 of DESNZ' CCS funding remains unallocated. Any future projects will mostly come online post-2030.</p>`,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=1007044354#gid=1007044354'
  },
  extraLevySharePostBICS: {
    categoryId: 'newLevies',
    name: 'Extra levy share post-BICS',
    value2025: 0,
    value2030: 5,
    description: `
      <p>The British Industrial Competitiveness Scheme reduces levy costs on heavy industry from 2027.</p>
      <p>In the absence of additional action, businesses and households will pay a small increased share of levies.</p>
      <p>NOTE: extra ETS revenues may reduce this burden, and it is also unclear what proportion of indsutrial consumption will be eligible for BICs. This is therefore a high uncertainty estimate.</p>
    `,
    link: 'https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=187489667#gid=187489667'
  },
  ncc: {
    categoryId: 'newLevies',
    name: 'NCC',
    value2025: 3,
    value2030: 7,
    description: `
      <p>The Network Charging Compensation scheme reduces levy costs on Energy Intensive Industries (EII), which spreads additional cost over businesses and households.</p>
    `
  },

  // VAT
  vat: {
    categoryId: 'misc', // VAT is grouped under misc for color coding
    name: 'VAT @ 5%',
    value2025: 46,
    value2030: 54,
    description: `
      <p>VAT on electricity and gas bills has been 5% since 1994.</p>
      <p>It scales proportionally with the rest of the bill.</p>`
  }
};

// Calculate totals for each year
export const calculateTotal = (components) => {
  return Object.values(components).reduce((sum, component) => sum + component.value2025, 0);
};

export const calculateTotal2030 = (components) => {
  return Object.values(components).reduce((sum, component) => sum + component.value2030, 0);
};

// Get aggregated data by category (for collapsed view)
export const getAggregatedByCategory = (components = billComponents) => {
  const aggregated = {};

  Object.entries(components).forEach(([id, data]) => {
    const categoryId = data.categoryId;

    if (!aggregated[categoryId]) {
      aggregated[categoryId] = {
        categoryId,
        name: getCategoryDisplayName(categoryId),
        value2025: 0,
        value2030: 0,
        componentIds: [],
        description: `<p>Click to see breakdown of ${getCategoryDisplayName(categoryId)} components.</p>`
      };
    }

    aggregated[categoryId].value2025 += data.value2025;
    aggregated[categoryId].value2030 += data.value2030;
    aggregated[categoryId].componentIds.push(id);
  });

  return aggregated;
};

// Get category display name
export const getCategoryDisplayName = (categoryId) => {
  const names = {
    wholesale: 'Wholesale',
    network: 'Network Costs',
    renewables: 'Generation Subsidies',
    misc: 'Misc',
    newLevies: 'New Levies'
  };
  return names[categoryId] || categoryId;
};

// Get data for Total view (two bars: Today and 2030)
export const getTotalViewData = (components = billComponents) => {
  const componentList = Object.entries(components).map(([id, data]) => ({
    id,
    ...data
  }));

  return {
    today: componentList.filter(c => c.value2025 > 0),
    forecast2030: componentList.filter(c => c.value2030 > 0)
  };
};

// Get data for Delta view (two bars: Increases and Decreases)
export const getDeltaViewData = (components = billComponents) => {
  const componentList = Object.entries(components).map(([id, data]) => ({
    id,
    ...data,
    delta: data.value2030 - data.value2025
  }));

  return {
    increases: componentList.filter(c => c.delta > 0),
    decreases: componentList.filter(c => c.delta < 0)
  };
};

// Scenario calculation functions (to be implemented with user-provided rules)
export const applyScenarios = (baseComponents, scenarios) => {
  let adjustedComponents = JSON.parse(JSON.stringify(baseComponents)); // Deep copy

  if (scenarios.removeVAT) {
    adjustedComponents = removeVAT(adjustedComponents);
  }

  if (scenarios.moveECOToTax) {
    adjustedComponents = moveECOToTax(adjustedComponents);
  }

  if (scenarios.reindexROandFiT) {
    adjustedComponents = reindexROandFiT(adjustedComponents);
  }

  return adjustedComponents;
};

// Scenario functions - placeholder implementations
const removeVAT = (components) => {
  // User will provide calculation rules
  const adjusted = { ...components };
  adjusted.vat = { ...adjusted.vat, value2030: 0 };
  return adjusted;
};

const moveECOToTax = (components) => {
  // User will provide calculation rules
  const adjusted = { ...components };
  adjusted.eco = { ...adjusted.eco, value2030: 0 };
  return adjusted;
};

const reindexROandFiT = (components) => {
  const adjusted = { ...components };

  // Apply FiT savings: £3.46
  adjusted.feedInTariffs = {
    ...adjusted.feedInTariffs,
    value2030: adjusted.feedInTariffs.value2030 - 3.46
  };

  // Apply RO savings: £6.94
  adjusted.renewablesObligation = {
    ...adjusted.renewablesObligation,
    value2030: adjusted.renewablesObligation.value2030 - 6.94
  };

  return adjusted;
};

// Scenario descriptions for hover tooltips
export const scenarioDescriptions = {
  removeVAT: `<p><strong>Remove VAT</strong></p>
    <p>Removes the 5% VAT charge from electricity bills. This would reduce bills but decrease government tax revenue.</p>`,

  moveECOToTax: `<p><strong>Move ECO to tax</strong></p>
    <p>Removes the Energy Company Obligation costs from electricity bills and funds it through general taxation instead.</p>`,

  reindexROandFiT: `<p><strong>Re-index RO & FiT</strong></p>
    <p>Adjusts the inflation indexation of Renewables Obligation and Feed-in Tariffs schemes to reduce future costs. This represents the strong "option 2" adjustment in the government's current consultation. </p>`
};
